import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/i18n";
import { toAbsoluteUrl } from "@/utils";
import {
  DataGrid,
  DataGridColumnHeader,
  DataGridColumnVisibility,
  DataGridRowSelect,
  DataGridRowSelectAll,
  KeenIcon,
  useDataGrid,
  Menu,
  MenuItem,
  MenuToggle,
} from "@/components";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DropdownCard1 } from "@/partials/dropdowns/general";
import { Link } from "react-router-dom";

const getNestedValue = (obj, path) => path.split('.').reduce((o, key) => (o ? o[key] : "-"), obj);

const ContactMatters = ({ tableTitle, tableData, tableColumns }) => {
  const { isRTL } = useLanguage();
  const storageFilterId = "members-filter";

  const ColumnInputFilter = ({ column }) => (
    <Input
      placeholder="Filter..."
      value={column.getFilterValue() ?? ""}
      onChange={(event) => column.setFilterValue(event.target.value)}
      className="h-9 w-full max-w-40"
    />
  );

  const columns = useMemo(() => {
    if (!tableColumns || tableColumns.length === 0) return [];

    const dynamicColumns = tableColumns.map((col) => ({
      accessorKey: col.accessorKey,
      id: col.accessorKey,
      header: ({ column }) => (
        <DataGridColumnHeader
          title={col.header}
          filter={<ColumnInputFilter column={column} />}
          column={column}
        />
      ),
      enableSorting: col.sortable !== false,
      cell: (info) => {
        const value = getNestedValue(info.row.original, col.accessorKey);

        if (col.accessorKey === "matterName")  {
          console.log('value', value)
          console.log('info.row.original,', info.row.original,)
            return (
                <Link to={`/matters/detail/${info.row.original.id}`} className="text-primary">{value}</Link>
            )
        }

        // Handle "assignedTo"
        if (col.accessorKey === "assignedTo") {
          return (
            <div className="flex items-center gap-2.5">
              <div className="shrink-0">
                <img
                  src={toAbsoluteUrl(`/media/avatars/${value.avatar}`)}
                  className="h-9 rounded-full"
                  alt=""
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="leading-none font-medium text-sm text-gray-900">{value.name}</span>
              </div>
            </div>
          );
        }

        // Handle "status" as a pill
        if (col.accessorKey === "tags") {
            return (
              <div className="flex flex-wrap gap-1">
                {value?.split(",").map((tag, index) => (
                  <span key={index} className="badge badge-pill badge-outline badge-primary">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            );
        }

        if (col.accessorKey === "status") {
            const statusColors = {
              Open: "badge-success",
              Pending: "badge-primary",
              Closed: "badge-danger",
            };
  
            return (
              // <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[value] || "bg-gray-300"}`}>
              //   {value}
              // </span>
              <span className={`badge badge-pill badge-outline ${statusColors[value]} gap-1 items-center`}>
              <span className={`badge badge-dot size-1.5 ${statusColors[value]}`}></span>
              {value}
            </span>
              // if (col.accessorKey === "status" && value) {
              //   return (
              //     <span className={`badge badge-pill badge-outline ${value.variant} gap-1 items-center`}>
              //       <span className={`badge badge-dot size-1.5 ${value.variant}`}></span>
              //       {value.label}
              //     </span>
              //   );
              // }
            );
          }

        return value || "-";
      },
      meta: { headerClassName: col.className || "min-w-[120px]" },
    }));

    return [
      {
        accessorKey: "id",
        header: () => <DataGridRowSelectAll />,
        cell: ({ row }) => <DataGridRowSelect row={row} />,
        enableSorting: false,
        enableHiding: false,
        meta: { headerClassName: "w-0" },
      },
    //   {
    //     accessorKey: "fullName",
    //     header: "Full Name",
    //     enableSorting: true,
    //     cell: (info) => {
    //       const { firstName, lastName } = info.row.original;
    //       return <Link to={`/contacts/${info.row.original.id}`} className="text-primary">{firstName} {lastName}</Link>;
    //     },
    //     meta: { headerClassName: "min-w-[160px]" },
    //   },
      ...dynamicColumns,
      {
        id: "actions",
        header: () => "",
        enableSorting: false,
        cell: () => (
          <Menu className="items-stretch">
            <MenuItem
              toggle="dropdown"
              trigger="click"
              dropdownProps={{
                placement: isRTL() ? "bottom-start" : "bottom-end",
                modifiers: [{ name: "offset", options: { offset: isRTL() ? [0, -10] : [0, 10] } }],
              }}
            >
              <MenuToggle className="btn btn-sm btn-icon btn-light btn-clear">
                <KeenIcon icon="dots-vertical" />
              </MenuToggle>
              {DropdownCard1()}
            </MenuItem>
          </Menu>
        ),
        meta: { headerClassName: "w-[60px]" },
      },
    ];
  }, [tableColumns, isRTL]);

  const data = useMemo(() => tableData || [], [tableData]);

  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem(storageFilterId) || "");

  useEffect(() => {
    console.log('tableData', tableData)
    localStorage.setItem(storageFilterId, searchTerm);
  }, [searchTerm]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter((row) =>
      Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, data]);

  const handleRowSelection = (state) => {
    const selectedRowIds = Object.keys(state);
    if (selectedRowIds.length > 0) {
      toast(`Total ${selectedRowIds.length} selected.`, {
        description: `Selected row IDs: ${selectedRowIds}`,
        action: { label: "Undo", onClick: () => console.log("Undo") },
      });
    }
  };

  const Toolbar = () => {
    const { table } = useDataGrid();
    return (
      <div className="card-header px-5 py-5 border-b-0 flex-wrap gap-2">
        <h3 className="card-title">{tableTitle}</h3>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <KeenIcon
              icon="magnifier"
              className="leading-none text-md text-gray-500 absolute top-1/2 start-0 -translate-y-1/2 ml-3"
            />
            <input
              type="text"
              placeholder="Search Members"
              className="input input-sm ps-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <DataGridColumnVisibility table={table} />
          <Link to={"/contacts/create"} className="btn btn-xs btn-sm btn-light">
            <KeenIcon icon="plus" className="w-fit btn-icon" />
            Add New
          </Link>
        </div>
      </div>
    );
  };

  return (
    <DataGrid
      columns={columns}
      data={filteredData}
      rowSelection={true}
      onRowSelectionChange={handleRowSelection}
      pagination={{ size: 5 }}
      sorting={[{ id: "email", desc: false }]}
      toolbar={<Toolbar />}
      layout={{ card: true }}
    />
  );
};

export { ContactMatters };